import React from "react";
import { Row, Col } from "react-bootstrap";
import { Icon } from "@iconify/react";
import styles from './AccessosWiki.module.scss';

/**
 * 🔔 Wiki - Seção Notificações
 * 
 * Documentação completa sobre o sistema de notificações da plataforma.
 * Explica como usar, configurar preferências e entender os diferentes tipos.
 * Voltada para produtores (não técnica).
 */
const NotificacoesWiki = () => {
  return (
    <div className={styles.wikiAccessos}>
      {/* Hero Section */}
      <section className={styles.heroSection}>
        <div className={styles.heroBadge}>
          <Icon icon="mdi:bell" />
          <span>Sistema de Notificações</span>
        </div>
        <p className={styles.heroText}>
          Fique informado sobre tudo que acontece na sua conta em tempo real.
          Novas vendas, comissões, solicitações de afiliação e muito mais.
        </p>
      </section>

      {/* O que são Notificações */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>O que são Notificações?</span>
          <p className={styles.sectionSubtitle}>
            Seu sistema de alertas em tempo real
          </p>
        </div>

        <div className={styles.infoCard}>
          <Icon icon="mdi:information" className={styles.infoIcon} />
          <div>
            <p>
              <strong>Notificações</strong> são alertas que você recebe quando algo importante 
              acontece na sua conta: uma nova venda, uma comissão recebida, uma solicitação 
              de afiliação, um saque aprovado, e muito mais.
            </p>
            <p style={{ marginTop: '12px', marginBottom: 0 }}>
              O sistema funciona em <strong>tempo real</strong> - você recebe as notificações 
              instantaneamente, sem precisar recarregar a página. Tudo acontece 
              automaticamente!
            </p>
          </div>
        </div>
      </section>

      {/* Como Acessar */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Como Acessar Notificações?</span>
          <p className={styles.sectionSubtitle}>
            Encontre o ícone do sino no header
          </p>
        </div>

        <Row className="g-4">
          <Col lg={12}>
            <div className={styles.participantCard}>
              <div className={`${styles.cardIcon} ${styles.iconPlatform}`}>
                <Icon icon="mdi:bell" />
              </div>
              <div className={styles.cardContent}>
                <span className={styles.cardTitle}>Ícone do Sino (🔔)</span>
                <p>
                  No <strong>canto superior direito</strong> da tela, você verá um ícone de sino 
                  com um <strong>contador vermelho</strong> mostrando quantas notificações não lidas você tem.
                </p>
                <div className={styles.exampleBox}>
                  <Icon icon="mdi:bell-badge" />
                  <span><strong>Exemplo:</strong> 🔔 [5] = 5 notificações não lidas</span>
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </section>

      {/* Dropdown de Notificações */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Dropdown de Notificações</span>
          <p className={styles.sectionSubtitle}>
            Visualização rápida das últimas notificações
          </p>
        </div>

        <Row className="g-4">
          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configNumber}>1</div>
              <div className={styles.configIcon}>
                <Icon icon="mdi:bell" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.cardTitle}>
                  Clique no Ícone
                </span>
                <p style={{ fontSize: '1rem', lineHeight: '1.7', marginBottom: 0 }}>
                  Clique no <strong>ícone do sino (🔔)</strong> no header para abrir o dropdown 
                  com as últimas <strong>10 notificações não lidas</strong>.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configNumber}>2</div>
              <div className={styles.configIcon}>
                <Icon icon="mdi:cursor-default-click" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.cardTitle}>
                  Clique em uma Notificação
                </span>
                <p style={{ fontSize: '1rem', lineHeight: '1.7', marginBottom: 0 }}>
                  Ao clicar em uma notificação, ela é <strong>marcada como lida automaticamente</strong> 
                  e você é <strong>direcionado para a tela específica</strong> relacionada.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={12}>
            <div className={styles.configCard}>
              <div className={styles.configNumber}>3</div>
              <div className={styles.configIcon}>
                <Icon icon="mdi:menu" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.cardTitle}>
                  Ações Disponíveis
                </span>
                <Row className="g-3 mt-2">
                  <Col md={6}>
                    <div style={{ 
                      background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.05), rgba(37, 99, 235, 0.05))',
                      borderRadius: '12px',
                      padding: '1rem',
                      border: '1px solid rgba(59, 130, 246, 0.1)'
                    }}>
                      <div className="d-flex align-items-center mb-2 gap-2">
                        <Icon icon="mdi:check-all" style={{ color: '#3b82f6', fontSize: '20px', marginBottom: '8px' }} />
                        <strong style={{ display: 'block', marginBottom: '4px' }}>Marcar todas como lidas</strong>
                      </div>
                      <span style={{ fontSize: '0.9rem' }}>Marca todas as notificações do dropdown de uma vez</span>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div style={{ 
                      background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.05), rgba(37, 99, 235, 0.05))',
                      borderRadius: '12px',
                      padding: '1rem',
                      border: '1px solid rgba(59, 130, 246, 0.1)'
                    }}>
                      <div className="d-flex align-items-center mb-2 gap-2">
                        <Icon icon="mdi:view-list" style={{ color: '#3b82f6', fontSize: '20px', marginBottom: '8px' }} />
                        <strong style={{ display: 'block', marginBottom: '4px' }}>Ver todas as notificações</strong>
                      </div>
                      <span style={{ fontSize: '0.9rem' }}>Abre o drawer completo com todas as notificações</span>
                    </div>
                  </Col>
                </Row>
              </div>
            </div>
          </Col>
        </Row>
      </section>

      {/* Drawer Completo */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Drawer de Notificações Completo</span>
          <p className={styles.sectionSubtitle}>
            Visualização completa com filtros e busca
          </p>
        </div>

        <Row className="g-4">
          <Col lg={6}>
            <div className={styles.participantCard}>
              <div className={`${styles.cardIcon} ${styles.iconAffiliate}`}>
                <Icon icon="mdi:filter" />
              </div>
              <div className={styles.cardContent}>
                <div className={`${styles.cardBadge} ${styles.badgeAffiliate}`}>Filtros</div>
                <span className={styles.cardTitle}>Não Lidas / Lidas</span>
                <p>
                  O drawer abre mostrando <strong>"Não Lidas"</strong> por padrão. 
                  Você pode alternar entre não lidas e lidas para organizar melhor.
                </p>
                <div className={styles.exampleBox}>
                  <Icon icon="mdi:bell-badge" />
                  <span><strong>Exemplo:</strong> [ 🔔 Não Lidas (5) ] [ ✓ Lidas (23) ]</span>
                </div>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.participantCard}>
              <div className={`${styles.cardIcon} ${styles.iconSupplier}`}>
                <Icon icon="mdi:magnify" />
              </div>
              <div className={styles.cardContent}>
                <div className={`${styles.cardBadge} ${styles.badgeSupplier}`}>Busca</div>
                <span className={styles.cardTitle}>Encontrar Notificações</span>
                <p>
                  Use o campo de busca para encontrar notificações específicas. 
                  Digite palavras-chave do <strong>título</strong> ou <strong>mensagem</strong>.
                </p>
                <div className={styles.exampleBox}>
                  <Icon icon="mdi:lightbulb" />
                  <span><strong>Dica:</strong> Busca filtra em tempo real</span>
                </div>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.participantCard}>
              <div className={`${styles.cardIcon} ${styles.iconLogistics}`}>
                <Icon icon="mdi:file-document-multiple" />
              </div>
              <div className={styles.cardContent}>
                <div className={`${styles.cardBadge} ${styles.badgeLogistics}`}>Paginação</div>
                <span className={styles.cardTitle}>20 por Página</span>
                <p>
                  O sistema mostra <strong>20 notificações por página</strong>. 
                  Use os botões "Anterior" e "Próxima" para navegar.
                </p>
                <div className={styles.exampleBox}>
                  <Icon icon="mdi:information" />
                  <span><strong>Info:</strong> Página X de Y (Z não lidas/lidas)</span>
                </div>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.participantCard}>
              <div className={`${styles.cardIcon} ${styles.iconProducer}`}>
                <Icon icon="mdi:calendar-range" />
              </div>
              <div className={styles.cardContent}>
                <div className={`${styles.cardBadge} ${styles.badgeProducer}`}>Período</div>
                <span className={styles.cardTitle}>Últimos 30 Dias</span>
                <p>
                  O sistema mostra apenas notificações dos <strong>últimos 30 dias</strong> 
                  para melhor performance. Notificações antigas são preservadas no banco.
                </p>
                <div className={styles.exampleBox}>
                  <Icon icon="mdi:database" style={{ color: '#10b981' }} />
                  <span><strong>Importante:</strong> Histórico completo preservado</span>
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </section>

      {/* Tipos de Notificações */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Tipos de Notificações e Navegação</span>
          <p className={styles.sectionSubtitle}>
            Cada tipo leva você para a tela específica
          </p>
        </div>

        <div className={styles.infoCard}>
          <Icon icon="mdi:information" className={styles.infoIcon} />
          <div>
            <p>
              Quando você <strong>clica em uma notificação</strong>, o sistema faz duas coisas:
            </p>
            <ol style={{ marginTop: '12px', paddingLeft: '20px' }}>
              <li>✅ <strong>Marca como lida</strong> automaticamente</li>
              <li>🧭 <strong>Navega para a tela específica</strong> relacionada</li>
            </ol>
          </div>
        </div>

        <Row className="g-4 mt-3">
          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={`${styles.configIcon} ${styles.iconPlatform}`}>
                <Icon icon="mdi:cart" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.stepTitle}>
                  💰 Nova Venda
                </span>
                <p style={{ fontSize: '0.95rem', lineHeight: '1.6', marginBottom: 0 }}>
                  Leva para <strong>Tela de Vendas</strong> onde você pode ver todos os detalhes da venda.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={`${styles.configIcon} ${styles.iconAffiliate}`}>
                <Icon icon="mdi:cash-multiple" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.stepTitle}>
                  💸 Comissão Recebida
                </span>
                <p style={{ fontSize: '0.95rem', lineHeight: '1.6', marginBottom: 0 }}>
                  Leva para <strong>Dashboard de Comissões</strong> para ver detalhes da comissão.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={`${styles.configIcon} ${styles.iconSupplier}`}>
                <Icon icon="mdi:account-network" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.stepTitle}>
                  🤝 Solicitação de Afiliação
                </span>
                <p style={{ fontSize: '0.95rem', lineHeight: '1.6', marginBottom: 0 }}>
                  Leva para <strong>Meus Afiliados</strong> para aprovar ou rejeitar.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={`${styles.configIcon} ${styles.iconLogistics}`}>
                <Icon icon="mdi:bank-transfer" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.stepTitle}>
                  💳 Saque Aprovado/Rejeitado
                </span>
                <p style={{ fontSize: '0.95rem', lineHeight: '1.6', marginBottom: 0 }}>
                  Leva para <strong>Tela de Saques</strong> para ver o status do saque.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={`${styles.configIcon} ${styles.iconPlatform}`}>
                <Icon icon="mdi:ticket-account" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.stepTitle}>
                  🎫 Ticket Respondido
                </span>
                <p style={{ fontSize: '0.95rem', lineHeight: '1.6', marginBottom: 0 }}>
                  Leva para <strong>Tela de Tickets</strong> para ver a resposta.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={`${styles.configIcon} ${styles.iconAffiliate}`}>
                <Icon icon="mdi:wallet" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.stepTitle}>
                  💰 Saldo Disponível
                </span>
                <p style={{ fontSize: '0.95rem', lineHeight: '1.6', marginBottom: 0 }}>
                  Leva para <strong>Tela de Saques</strong> para solicitar saque.
                </p>
              </div>
            </div>
          </Col>
        </Row>
      </section>

      {/* Marcar como Lida */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Como Marcar como Lida?</span>
          <p className={styles.sectionSubtitle}>
            Três formas diferentes de organizar suas notificações
          </p>
        </div>

        <Row className="g-4">
          <Col lg={4}>
            <div className={styles.stepCard}>
              <div className={styles.stepHeader}>
                <div className={styles.stepNumber}>1</div>
                <div className={styles.stepIcon}>
                  <Icon icon="mdi:cursor-default-click" />
                </div>
              </div>
              <div className={styles.stepContent}>
                <span className={styles.stepTitle}>Clicar na Notificação</span>
                <p>
                  Ao clicar em qualquer notificação, ela é <strong>marcada como lida automaticamente </strong> 
                  e você é direcionado para a tela relacionada.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={4}>
            <div className={styles.stepCard}>
              <div className={styles.stepHeader}>
                <div className={styles.stepNumber}>2</div>
                <div className={styles.stepIcon}>
                  <Icon icon="mdi:check" />
                </div>
              </div>
              <div className={styles.stepContent}>
                <span className={styles.stepTitle}>Botão ✓ (Check)</span>
                <p>
                  Em notificações <strong>não lidas</strong>, aparece um botão <strong>✓</strong> no hover. 
                  Clique para marcar como lida <strong>sem navegar</strong>.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={4}>
            <div className={styles.stepCard}>
              <div className={styles.stepHeader}>
                <div className={styles.stepNumber}>3</div>
                <div className={styles.stepIcon}>
                  <Icon icon="mdi:check-all" />
                </div>
              </div>
              <div className={styles.stepContent}>
                <span className={styles.stepTitle}>Marcar Todas</span>
                <p>
                  No dropdown, clique em <strong>"Marcar todas como lidas"</strong> para marcar 
                  todas as notificações do dropdown de uma vez.
                </p>
              </div>
            </div>
          </Col>
        </Row>
      </section>

      {/* Configurar Preferências */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Configurar Preferências</span>
          <p className={styles.sectionSubtitle}>
            Personalize quais notificações você quer receber
          </p>
        </div>

        <div className={styles.configCard}>
          <div className={styles.configNumber}>1</div>
          <div className={styles.configIcon}>
            <Icon icon="mdi:cog" />
          </div>
          <div className={styles.configContent}>
            <span className={styles.cardTitle}>
              Acessar Preferências
            </span>
            <p style={{ fontSize: '1rem', lineHeight: '1.7', marginBottom: '1rem' }}>
              Clique no ícone do sino (🔔) e depois no <strong>ícone de engrenagem (⚙️)</strong> 
              no canto superior direito do dropdown.
            </p>
            <div style={{ 
              background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.05), rgba(217, 119, 6, 0.05))',
              borderRadius: '12px',
              padding: '1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              border: '1px solid rgba(245, 158, 11, 0.1)'
            }}>
              <Icon icon="mdi:lightbulb" style={{ color: '#F59E0B', fontSize: '20px', flexShrink: 0 }} />
              <span style={{ fontSize: '0.95rem' }}>
                Você pode configurar: Ativar/Desativar, Alertas (toasts) e Email (futuro)
              </span>
            </div>
          </div>
        </div>

        <Row className="g-4 mt-3">
          <Col lg={6}>
            <div className={styles.participantCard}>
              <div className={`${styles.cardIcon} ${styles.iconPlatform}`}>
                <Icon icon="mdi:alert-circle" />
              </div>
              <div className={styles.cardContent}>
                <div className={`${styles.cardBadge} ${styles.badgePlatform}`}>Alta Prioridade</div>
                <span className={styles.cardTitle}>Mostram Toast</span>
                <p>
                  Essas notificações aparecem como <strong>toast na tela</strong> se você tiver 
                  alertas habilitados:
                </p>
                <ul style={{ marginTop: '8px', paddingLeft: '20px', marginBottom: 0 }}>
                  <li>Ticket Respondido</li>
                  <li>Solicitação de Afiliação</li>
                  <li>Saque Aprovado/Rejeitado</li>
                </ul>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.participantCard}>
              <div className={`${styles.cardIcon} ${styles.iconAffiliate}`}>
                <Icon icon="mdi:bell-outline" />
              </div>
              <div className={styles.cardContent}>
                <div className={`${styles.cardBadge} ${styles.badgeAffiliate}`}>Baixa Prioridade</div>
                <span className={styles.cardTitle}>Apenas Dropdown</span>
                <p>
                  Essas notificações aparecem <strong>apenas no dropdown</strong>, sem toast:
                </p>
                <ul style={{ marginTop: '8px', paddingLeft: '20px', marginBottom: 0 }}>
                  <li>Nova Venda</li>
                  <li>Comissão Recebida</li>
                  <li>Saldo Disponível</li>
                </ul>
              </div>
            </div>
          </Col>
        </Row>
      </section>

      {/* Indicadores Visuais */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Indicadores Visuais</span>
          <p className={styles.sectionSubtitle}>
            Entenda os símbolos e cores
          </p>
        </div>

        <Row className="g-4">
          <Col lg={4}>
            <div className={styles.tipCard} style={{ padding: '20px' }}>
              <Icon icon="mdi:circle" style={{ color: '#3b82f6', fontSize: '24px' }} />
              <div>
                <strong>Bolinha Azul</strong>
                <p style={{ marginTop: '8px', marginBottom: 0 }}>
                  Notificações <strong>não lidas</strong> têm uma bolinha azul no lado esquerdo. 
                  Facilita identificar rapidamente o que precisa atenção.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={4}>
            <div className={styles.tipCard} style={{ padding: '20px' }}>
              <Icon icon="mdi:bell-badge" style={{ color: '#dc3545', fontSize: '24px' }} />
              <div>
                <strong>Contador Vermelho</strong>
                <p style={{ marginTop: '8px', marginBottom: 0 }}>
                  O ícone do sino mostra um <strong>badge vermelho</strong> com o número de não lidas. 
                  Exemplo: 🔔 [5] = 5 notificações não lidas.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={4}>
            <div className={styles.tipCard} style={{ padding: '20px' }}>
              <Icon icon="mdi:clock-outline" style={{ color: '#6c757d', fontSize: '24px' }} />
              <div>
                <strong>Timestamps Relativos</strong>
                <p style={{ marginTop: '8px', marginBottom: 0 }}>
                  Cada notificação mostra há quanto tempo foi criada: 
                  "Agora mesmo", "2min atrás", "5h atrás", "3d atrás".
                </p>
              </div>
            </div>
          </Col>
        </Row>
      </section>

      {/* Perguntas Frequentes */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Perguntas Frequentes</span>
          <p className={styles.sectionSubtitle}>
            Dúvidas comuns sobre notificações
          </p>
        </div>

        <div className={styles.faqContainer}>
          <div className={styles.faqItem}>
            <Icon icon="mdi:help-circle" className={styles.faqIcon} />
            <div>
              <span className={styles.faqQuestion}>Por que não vejo notificações antigas?</span>
              <p className={styles.faqAnswer}>
                O sistema mostra apenas notificações dos <strong>últimos 30 dias</strong> para melhor 
                performance. Notificações mais antigas estão preservadas no banco, mas não aparecem 
                na interface.
              </p>
            </div>
          </div>

          <div className={styles.faqItem}>
            <Icon icon="mdi:help-circle" className={styles.faqIcon} />
            <div>
              <span className={styles.faqQuestion}>Posso deletar notificações?</span>
              <p className={styles.faqAnswer}>
                Não. O sistema <strong>preserva todas as notificações</strong> para auditoria e 
                rastreabilidade. Você pode apenas <strong>marcar como lida</strong> usando o botão ✓.
              </p>
            </div>
          </div>

          <div className={styles.faqItem}>
            <Icon icon="mdi:help-circle" className={styles.faqIcon} />
            <div>
              <span className={styles.faqQuestion}>Como desabilitar notificações de um tipo específico?</span>
              <p className={styles.faqAnswer}>
                Acesse as <strong>Preferências</strong> (ícone ⚙️) e desative o tipo desejado. 
                Você continuará recebendo, mas não verá toasts ou alertas.
              </p>
            </div>
          </div>

          <div className={styles.faqItem}>
            <Icon icon="mdi:help-circle" className={styles.faqIcon} />
            <div>
              <span className={styles.faqQuestion}>Por que algumas notificações mostram toast e outras não?</span>
              <p className={styles.faqAnswer}>
                Depende da <strong>prioridade</strong> e das suas <strong>preferências</strong>:
                Alta prioridade (tickets, saques) → Mostram toast se alertas estiverem habilitados.
                Baixa prioridade (vendas, comissões) → Apenas no dropdown.
              </p>
            </div>
          </div>

          <div className={styles.faqItem}>
            <Icon icon="mdi:help-circle" className={styles.faqIcon} />
            <div>
              <span className={styles.faqQuestion}>O que acontece se eu clicar em uma notificação?</span>
              <p className={styles.faqAnswer}>
                Duas coisas: 1) A notificação é <strong>marcada como lida</strong> automaticamente. 
                2) Você é <strong>direcionado para a tela específica</strong> relacionada 
                (ex: venda → tela de vendas).
              </p>
            </div>
          </div>

          <div className={styles.faqItem}>
            <Icon icon="mdi:help-circle" className={styles.faqIcon} />
            <div>
              <span className={styles.faqQuestion}>Posso ver notificações de outros usuários?</span>
              <p className={styles.faqAnswer}>
                Não. O sistema é <strong>100% seguro</strong> - cada usuário vê apenas suas próprias 
                notificações. Autenticação obrigatória garante privacidade total.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Dicas Importantes */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Dicas Importantes</span>
        </div>

        <Row className="g-3">
          <Col lg={6}>
            <div className={styles.tipCard} style={{ padding: '20px' }}>
              <Icon icon="mdi:filter" style={{ color: '#10b981', fontSize: '24px' }} />
              <div>
                <strong>Organização Rápida</strong>
                <p style={{ marginTop: '8px', marginBottom: 0 }}>
                  Use o filtro <strong>"Não Lidas"</strong> para ver apenas o que precisa atenção. 
                  Use a <strong>busca</strong> para encontrar notificações antigas específicas.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.tipCard} style={{ padding: '20px' }}>
              <Icon icon="mdi:map-marker-path" style={{ color: '#3b82f6', fontSize: '24px' }} />
              <div>
                <strong>Navegação Rápida</strong>
                <p style={{ marginTop: '8px', marginBottom: 0 }}>
                  Clique diretamente na notificação para ir para a tela relacionada. 
                  Não precisa procurar manualmente onde está a informação.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.tipCard} style={{ padding: '20px' }}>
              <Icon icon="mdi:database" style={{ color: '#8b5cf6', fontSize: '24px' }} />
              <div>
                <strong>Histórico Completo</strong>
                <p style={{ marginTop: '8px', marginBottom: 0 }}>
                  Todas as notificações são <strong>preservadas</strong> (nunca deletadas). 
                  Você pode consultar notificações antigas na aba <strong>"Lidas"</strong>.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.tipCard} style={{ padding: '20px' }}>
              <Icon icon="mdi:speedometer" style={{ color: '#f59e0b', fontSize: '24px' }} />
              <div>
                <strong>Performance</strong>
                <p style={{ marginTop: '8px', marginBottom: 0 }}>
                  O sistema mostra apenas <strong>últimos 30 dias</strong> para melhor performance. 
                  Use a <strong>paginação</strong> para navegar entre muitas notificações.
                </p>
              </div>
            </div>
          </Col>
        </Row>
      </section>
    </div>
  );
};

export default NotificacoesWiki;

